'use client';

import React from 'react';
import {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  FieldErrors,
} from 'react-hook-form';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Checkbox } from '@/components/ui/Checkbox';
import { Label } from '@/components/ui/Label';
import type { HealthLogType } from '@/features/health/types/healthTypes';

interface DynamicHealthFieldsProps {
  logType: HealthLogType | null;
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  errors: FieldErrors<any>;
}

// Field schema types for each log type
const FIELD_SCHEMAS: Record<string, FieldConfig[]> = {
  seizure: [
    { name: 'seizureType', label: 'Seizure Type', type: 'select', options: [
      { value: 'tonic-clonic', label: 'Tonic-Clonic (Grand Mal)' },
      { value: 'absence', label: 'Absence (Petit Mal)' },
      { value: 'focal', label: 'Focal/Partial' },
      { value: 'myoclonic', label: 'Myoclonic' },
      { value: 'atonic', label: 'Atonic (Drop)' },
      { value: 'febrile', label: 'Febrile' },
      { value: 'unknown', label: 'Unknown' },
    ]},
    { name: 'triggers', label: 'Possible Triggers', type: 'checkboxGroup', options: [
      { value: 'lack_of_sleep', label: 'Lack of sleep' },
      { value: 'stress', label: 'Stress' },
      { value: 'missed_medication', label: 'Missed medication' },
      { value: 'illness', label: 'Illness/Fever' },
      { value: 'flashing_lights', label: 'Flashing lights' },
      { value: 'overheating', label: 'Overheating' },
      { value: 'unknown', label: 'Unknown' },
    ]},
    { name: 'consciousness', label: 'Consciousness', type: 'select', options: [
      { value: 'lost', label: 'Lost consciousness' },
      { value: 'impaired', label: 'Impaired awareness' },
      { value: 'retained', label: 'Fully aware' },
    ]},
    { name: 'postictalState', label: 'Post-Ictal State', type: 'textarea', placeholder: 'Describe behavior/state after seizure...' },
    { name: 'emergencyServicesNeeded', label: 'Emergency services called', type: 'checkbox' },
  ],
  
  medication: [
    { name: 'medicationName', label: 'Medication Name', type: 'text', required: true },
    { name: 'dosage', label: 'Dosage', type: 'text', required: true, placeholder: 'e.g., 100mg' },
    { name: 'route', label: 'Route', type: 'select', options: [
      { value: 'oral', label: 'Oral' },
      { value: 'injection', label: 'Injection' },
      { value: 'topical', label: 'Topical' },
      { value: 'inhalation', label: 'Inhalation' },
      { value: 'rectal', label: 'Rectal' },
      { value: 'other', label: 'Other' },
    ]},
    { name: 'givenBy', label: 'Given By', type: 'text', placeholder: 'Parent, nurse, self...' },
    { name: 'sideEffects', label: 'Side Effects Observed', type: 'textarea', placeholder: 'Note any side effects...' },
    { name: 'wasScheduled', label: 'Scheduled dose', type: 'checkbox' },
  ],
  
  symptom: [
    { name: 'symptomType', label: 'Symptom', type: 'select', options: [
      { value: 'pain', label: 'Pain' },
      { value: 'nausea', label: 'Nausea/Vomiting' },
      { value: 'headache', label: 'Headache' },
      { value: 'fatigue', label: 'Fatigue' },
      { value: 'dizziness', label: 'Dizziness' },
      { value: 'breathing', label: 'Breathing difficulty' },
      { value: 'fever', label: 'Fever' },
      { value: 'rash', label: 'Rash' },
      { value: 'swelling', label: 'Swelling' },
      { value: 'other', label: 'Other' },
    ]},
    { name: 'bodyLocation', label: 'Body Location', type: 'text', placeholder: 'Where is the symptom?' },
    { name: 'qualityDescriptors', label: 'Quality', type: 'checkboxGroup', options: [
      { value: 'sharp', label: 'Sharp' },
      { value: 'dull', label: 'Dull' },
      { value: 'throbbing', label: 'Throbbing' },
      { value: 'constant', label: 'Constant' },
      { value: 'intermittent', label: 'Intermittent' },
      { value: 'burning', label: 'Burning' },
    ]},
    { name: 'reliefMeasures', label: 'Relief Measures Tried', type: 'textarea', placeholder: 'What helped?' },
  ],
  
  sleep: [
    { name: 'sleepQuality', label: 'Sleep Quality (1-10)', type: 'range', min: 1, max: 10 },
    { name: 'bedTime', label: 'Bed Time', type: 'time' },
    { name: 'wakeTime', label: 'Wake Time', type: 'time' },
    { name: 'nightWakings', label: 'Night Wakings', type: 'number', min: 0, max: 20 },
    { name: 'sleepAids', label: 'Sleep Aids Used', type: 'checkboxGroup', options: [
      { value: 'melatonin', label: 'Melatonin' },
      { value: 'medication', label: 'Prescription medication' },
      { value: 'weighted_blanket', label: 'Weighted blanket' },
      { value: 'white_noise', label: 'White noise' },
      { value: 'none', label: 'None' },
    ]},
    { name: 'sleepIssues', label: 'Sleep Issues', type: 'checkboxGroup', options: [
      { value: 'trouble_falling_asleep', label: 'Trouble falling asleep' },
      { value: 'trouble_staying_asleep', label: 'Trouble staying asleep' },
      { value: 'nightmares', label: 'Nightmares' },
      { value: 'sleep_apnea', label: 'Sleep apnea episodes' },
      { value: 'restless', label: 'Restless sleep' },
    ]},
  ],
  
  feeding: [
    { name: 'feedingType', label: 'Feeding Type', type: 'select', options: [
      { value: 'oral', label: 'Oral' },
      { value: 'g-tube', label: 'G-Tube' },
      { value: 'ng-tube', label: 'NG-Tube' },
      { value: 'breast', label: 'Breastfeeding' },
      { value: 'bottle', label: 'Bottle' },
      { value: 'mixed', label: 'Mixed' },
    ]},
    { name: 'amount', label: 'Amount', type: 'text', placeholder: 'e.g., 6 oz, 200ml' },
    { name: 'feedingDuration', label: 'Duration (minutes)', type: 'number', min: 0, max: 120 },
    { name: 'foodTypes', label: 'Food/Formula', type: 'textarea', placeholder: 'List foods or formula...' },
    { name: 'feedingIssues', label: 'Issues', type: 'checkboxGroup', options: [
      { value: 'refused', label: 'Refused' },
      { value: 'vomiting', label: 'Vomiting' },
      { value: 'gagging', label: 'Gagging' },
      { value: 'aspiration', label: 'Aspiration concern' },
      { value: 'slow', label: 'Very slow eating' },
    ]},
  ],
  
  therapy: [
    { name: 'therapyType', label: 'Therapy Type', type: 'select', required: true, options: [
      { value: 'pt', label: 'Physical Therapy (PT)' },
      { value: 'ot', label: 'Occupational Therapy (OT)' },
      { value: 'speech', label: 'Speech Therapy' },
      { value: 'aba', label: 'ABA Therapy' },
      { value: 'aquatic', label: 'Aquatic Therapy' },
      { value: 'hippo', label: 'Hippotherapy' },
      { value: 'music', label: 'Music Therapy' },
      { value: 'vision', label: 'Vision Therapy' },
      { value: 'feeding', label: 'Feeding Therapy' },
      { value: 'other', label: 'Other' },
    ]},
    { name: 'providerName', label: 'Therapist Name', type: 'text' },
    { name: 'location', label: 'Location', type: 'select', options: [
      { value: 'clinic', label: 'Clinic' },
      { value: 'home', label: 'Home' },
      { value: 'school', label: 'School' },
      { value: 'telehealth', label: 'Telehealth' },
    ]},
    { name: 'goalsWorkedOn', label: 'Goals Worked On', type: 'textarea', placeholder: 'What goals were addressed?' },
    { name: 'progressNotes', label: 'Progress Notes', type: 'textarea', placeholder: 'How did it go?' },
    { name: 'exercisesForHome', label: 'Home Exercises Assigned', type: 'textarea', placeholder: 'Exercises to practice at home...' },
  ],
  
  vital: [
    { name: 'temperature', label: 'Temperature (°F)', type: 'number', step: 0.1, min: 90, max: 110 },
    { name: 'heartRate', label: 'Heart Rate (bpm)', type: 'number', min: 30, max: 250 },
    { name: 'bloodPressureSystolic', label: 'Blood Pressure (Systolic)', type: 'number', min: 50, max: 250 },
    { name: 'bloodPressureDiastolic', label: 'Blood Pressure (Diastolic)', type: 'number', min: 30, max: 150 },
    { name: 'oxygenSaturation', label: 'Oxygen Saturation (%)', type: 'number', min: 70, max: 100 },
    { name: 'respiratoryRate', label: 'Respiratory Rate (per min)', type: 'number', min: 5, max: 60 },
    { name: 'weight', label: 'Weight (lbs)', type: 'number', step: 0.1, min: 1, max: 500 },
  ],
  
  mood: [
    { name: 'moodScore', label: 'Overall Mood (1-10)', type: 'range', min: 1, max: 10 },
    { name: 'moodDescriptors', label: 'Mood', type: 'checkboxGroup', options: [
      { value: 'happy', label: 'Happy' },
      { value: 'calm', label: 'Calm' },
      { value: 'anxious', label: 'Anxious' },
      { value: 'sad', label: 'Sad' },
      { value: 'irritable', label: 'Irritable' },
      { value: 'frustrated', label: 'Frustrated' },
      { value: 'tired', label: 'Tired' },
    ]},
    { name: 'behaviors', label: 'Behaviors Observed', type: 'checkboxGroup', options: [
      { value: 'self_harm', label: 'Self-injurious' },
      { value: 'aggression', label: 'Aggression' },
      { value: 'stimming', label: 'Stimming' },
      { value: 'tantrums', label: 'Tantrums' },
      { value: 'withdrawal', label: 'Withdrawal' },
      { value: 'hyperactive', label: 'Hyperactive' },
    ]},
    { name: 'triggers', label: 'Possible Triggers', type: 'textarea', placeholder: 'What may have caused this mood/behavior?' },
    { name: 'copingStrategies', label: 'Coping Strategies Used', type: 'textarea', placeholder: 'What helped?' },
  ],
};

interface FieldConfig {
  name: string;
  label: string;
  type: 'text' | 'number' | 'textarea' | 'select' | 'checkbox' | 'checkboxGroup' | 'time' | 'range';
  required?: boolean;
  placeholder?: string;
  options?: { value: string; label: string }[];
  min?: number;
  max?: number;
  step?: number;
}

export function DynamicHealthFields({
  logType,
  register,
  setValue,
  watch,
  errors,
}: DynamicHealthFieldsProps): React.ReactElement | null {
  if (!logType) return null;

  const fields = FIELD_SCHEMAS[logType.slug] || [];
  
  if (fields.length === 0) return null;

  const renderField = (field: FieldConfig) => {
    const fieldValue = watch(`data.${field.name}`);
    const error = errors?.data?.[field.name as keyof typeof errors.data];

    switch (field.type) {
      case 'text':
        return (
          <Input
            {...register(`data.${field.name}`, { required: field.required })}
            placeholder={field.placeholder}
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            {...register(`data.${field.name}`, { 
              required: field.required,
              valueAsNumber: true,
              min: field.min,
              max: field.max,
            })}
            step={field.step}
            min={field.min}
            max={field.max}
          />
        );

      case 'time':
        return (
          <Input
            type="time"
            {...register(`data.${field.name}`, { required: field.required })}
          />
        );

      case 'textarea':
        return (
          <Textarea
            {...register(`data.${field.name}`, { required: field.required })}
            placeholder={field.placeholder}
            rows={3}
          />
        );

      case 'select':
        return (
          <Select
            {...register(`data.${field.name}`, { required: field.required })}
          >
            <option value="">Select...</option>
            {field.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        );

      case 'checkbox':
        return (
          <div className="flex items-center gap-2">
            <Checkbox
              {...register(`data.${field.name}`)}
              id={`field-${field.name}`}
            />
            <label 
              htmlFor={`field-${field.name}`}
              className="text-sm text-navy cursor-pointer"
            >
              {field.label}
            </label>
          </div>
        );

      case 'checkboxGroup':
        const selectedValues = fieldValue || [];
        return (
          <div className="grid grid-cols-2 gap-2">
            {field.options?.map((opt) => (
              <div key={opt.value} className="flex items-center gap-2">
                <Checkbox
                  id={`${field.name}-${opt.value}`}
                  checked={selectedValues.includes(opt.value)}
                  onChange={(e) => {
                    const newValues = e.target.checked
                      ? [...selectedValues, opt.value]
                      : selectedValues.filter((v: string) => v !== opt.value);
                    setValue(`data.${field.name}`, newValues);
                  }}
                />
                <label
                  htmlFor={`${field.name}-${opt.value}`}
                  className="text-sm text-navy cursor-pointer"
                >
                  {opt.label}
                </label>
              </div>
            ))}
          </div>
        );

      case 'range':
        return (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <input
                type="range"
                {...register(`data.${field.name}`, { valueAsNumber: true })}
                min={field.min}
                max={field.max}
                className="flex-1 h-2 bg-cream rounded-lg appearance-none cursor-pointer"
              />
              <span className="ml-4 w-8 text-center font-medium text-navy">
                {fieldValue || field.min}
              </span>
            </div>
            <div className="flex justify-between text-xs text-warm-gray">
              <span>{field.min}</span>
              <span>{field.max}</span>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-4 p-4 bg-cream/50 rounded-lg">
      <h4 className="text-sm font-medium text-navy">
        {logType.name} Details
      </h4>
      
      {fields.map((field) => (
        <div key={field.name} className="space-y-1.5">
          {field.type !== 'checkbox' && (
            <Label htmlFor={`field-${field.name}`}>
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
          )}
          {renderField(field)}
          {errors?.data?.[field.name as keyof typeof errors.data] && (
            <p className="text-xs text-red-500">
              {field.label} is required
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

export default DynamicHealthFields;

